import { isNumber } from 'lodash';
import {
  OrderActionDto,
  OrderDurationDto,
  OrderTypeDto,
  OrderStatusIntDto,
  OrderTypeIntDto,
  OrderActionIntDto,
  OrderDurationIntDto,
} from 'src/dtos';

export enum OrderTypeLabel {
  Market = 'Market Order',
  Limit = 'Limit Order',
  StopMarket = 'Stop Market Order',
  StopLimit = 'Stop Limit Order',
  MarketOnClose = 'Market on Close Order',
}

export enum OrderActionLabel {
  Buy = 'Buy',
  Sell = 'Sell',
}

export enum OrderDurationLabel {
  Day = 'End of Day',
  GTC = 'Good Until Cancelled',
}

export enum OrderStatusLabel {
  WORKING = 'WORKING',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum OrderEventStatusLabel {
  Open = 'Open',
  PartiallyFilled = 'Partially Filled',
  Filled = 'Filled',
  DoneForDay = 'Done For Day',
  Cancelled = 'Cancelled',
  Replaced = 'Replaced',
  PendingCancel = 'Pending Cancel',
  Stopped = 'Stopped',
  Rejected = 'Rejected',
  Suspended = 'Suspended',
  PendingOpen = 'Pending Open',
  Calculated = 'Calculated',
  Expired = 'Expired',
  PendingReplace = 'Pending Replace',
  Saved = 'Saved',
  LiveUntriggered = 'Live Untriggered',
  Scheduled = 'Scheduled',
  OCOUntriggered = 'OCO Untriggered',
  CancelledUntriggered = 'Cancelled Untriggered',
  Queued = 'Queued',
  ReplaceInitiated = 'Replace Initiated',
  CancelInitiated = 'Cancel Initiated',
  CancelRejected = 'Cancel Rejected',
  ReplaceRejected = 'Replace Rejected',
  Busted = 'Busted',
  PreAllocated = 'Pre Allocated',
  Acknowledged = 'Acknowledged',
  PartiallyCancelled = 'Partially Cancelled',
}

export class OrderStatus {
  private _value: OrderStatusIntDto;
  private _label: OrderStatusLabel;

  constructor(status: OrderStatusIntDto) {
    this._value = status;
    this._label = this.findLabel();
  }

  private findLabel(): OrderStatusLabel {
    switch (this._value) {
      case OrderStatusIntDto.Filled:
        return OrderStatusLabel.COMPLETED;
      case OrderStatusIntDto.Cancelled:
      case OrderStatusIntDto.PartiallyCancelled:
        return OrderStatusLabel.CANCELLED;
      case OrderStatusIntDto.Rejected:
      case OrderStatusIntDto.ReplaceRejected:
      case OrderStatusIntDto.CancelRejected:
      case OrderStatusIntDto.Suspended:
      case OrderStatusIntDto.Expired:
      case OrderStatusIntDto.Busted:
        return OrderStatusLabel.REJECTED;
      case OrderStatusIntDto.New:
      case OrderStatusIntDto.PartiallyFilled:
      case OrderStatusIntDto.PendingNew:
      case OrderStatusIntDto.PendingCancel:
      case OrderStatusIntDto.Scheduled:
      case OrderStatusIntDto.CancelInitiated:
      case OrderStatusIntDto.Stopped:
      case OrderStatusIntDto.Calculated:
      case OrderStatusIntDto.DoneForDay:
      case OrderStatusIntDto.Saved:
      case OrderStatusIntDto.Acknowledged:
      case OrderStatusIntDto.Replaced:
      case OrderStatusIntDto.PendingReplace:
      case OrderStatusIntDto.LiveUntriggered:
      case OrderStatusIntDto.OCO_Untriggered:
      case OrderStatusIntDto.CancelledUntriggered:
      case OrderStatusIntDto.Initiated:
      case OrderStatusIntDto.ReplaceInitiated:
      case OrderStatusIntDto.PreAllocated:
        return OrderStatusLabel.WORKING;
    }
  }

  get value(): OrderStatusIntDto {
    return this._value;
  }

  get label(): OrderStatusLabel {
    return this._label;
  }

  get isWorking() {
    return this._label === OrderStatusLabel.WORKING;
  }

  get isCompleted() {
    return this._label === OrderStatusLabel.COMPLETED;
  }

  get isCancelled() {
    return this._label === OrderStatusLabel.CANCELLED;
  }

  get isRejected() {
    return this._label === OrderStatusLabel.REJECTED;
  }
}

export class OrderEventStatus {
  private _value: OrderStatusIntDto;
  private _label: OrderEventStatusLabel;

  constructor(status: OrderStatusIntDto) {
    this._value = status;
    this._label = this.findLabel();
  }

  private findLabel(): OrderEventStatusLabel {
    switch (this._value) {
      case OrderStatusIntDto.New:
        return OrderEventStatusLabel.Open;
      case OrderStatusIntDto.PartiallyFilled:
        return OrderEventStatusLabel.PartiallyFilled;
      case OrderStatusIntDto.Filled:
        return OrderEventStatusLabel.Filled;
      case OrderStatusIntDto.DoneForDay:
        return OrderEventStatusLabel.DoneForDay;
      case OrderStatusIntDto.Cancelled:
        return OrderEventStatusLabel.Cancelled;
      case OrderStatusIntDto.Replaced:
        return OrderEventStatusLabel.Replaced;
      case OrderStatusIntDto.PendingCancel:
        return OrderEventStatusLabel.PendingCancel;
      case OrderStatusIntDto.Stopped:
        return OrderEventStatusLabel.Stopped;
      case OrderStatusIntDto.Rejected:
        return OrderEventStatusLabel.Rejected;
      case OrderStatusIntDto.Suspended:
        return OrderEventStatusLabel.Suspended;
      case OrderStatusIntDto.PendingNew:
        return OrderEventStatusLabel.PendingOpen;
      case OrderStatusIntDto.Calculated:
        return OrderEventStatusLabel.Calculated;
      case OrderStatusIntDto.Expired:
        return OrderEventStatusLabel.Expired;
      case OrderStatusIntDto.PendingReplace:
        return OrderEventStatusLabel.PendingReplace;
      case OrderStatusIntDto.Saved:
        return OrderEventStatusLabel.Saved;
      case OrderStatusIntDto.LiveUntriggered:
        return OrderEventStatusLabel.LiveUntriggered;
      case OrderStatusIntDto.Scheduled:
        return OrderEventStatusLabel.Scheduled;
      case OrderStatusIntDto.OCO_Untriggered:
        return OrderEventStatusLabel.OCOUntriggered;
      case OrderStatusIntDto.CancelledUntriggered:
        return OrderEventStatusLabel.CancelledUntriggered;
      case OrderStatusIntDto.Initiated:
        return OrderEventStatusLabel.Queued;
      case OrderStatusIntDto.ReplaceInitiated:
        return OrderEventStatusLabel.ReplaceInitiated;
      case OrderStatusIntDto.CancelInitiated:
        return OrderEventStatusLabel.CancelInitiated;
      case OrderStatusIntDto.CancelRejected:
        return OrderEventStatusLabel.CancelRejected;
      case OrderStatusIntDto.ReplaceRejected:
        return OrderEventStatusLabel.ReplaceRejected;
      case OrderStatusIntDto.Busted:
        return OrderEventStatusLabel.Busted;
      case OrderStatusIntDto.PreAllocated:
        return OrderEventStatusLabel.PreAllocated;
      case OrderStatusIntDto.Acknowledged:
        return OrderEventStatusLabel.Acknowledged;
      case OrderStatusIntDto.PartiallyCancelled:
        return OrderEventStatusLabel.PartiallyCancelled;
    }
  }

  get value(): OrderStatusIntDto {
    return this._value;
  }

  get label(): OrderEventStatusLabel {
    return this._label;
  }

  get isCompleted() {
    return this._value === OrderStatusIntDto.Filled;
  }

  get isCancelled() {
    return this._value === OrderStatusIntDto.Cancelled || this._value === OrderStatusIntDto.PartiallyCancelled;
  }

  get isPendingNew() {
    return this._value === OrderStatusIntDto.PendingNew;
  }
}

export class OrderType {
  private _value: OrderTypeDto;
  private _label: OrderTypeLabel;

  constructor(type: OrderTypeIntDto | OrderTypeDto) {
    this._value = isNumber(type) ? this.mapTypeNumberToEnum(type) : (type as OrderTypeDto);
    this._label = this.mapTypeEnumToLabel(this._value);
  }

  private mapTypeNumberToEnum(type: OrderTypeIntDto): OrderTypeDto {
    switch (type) {
      case OrderTypeIntDto.Market:
        return OrderTypeDto.Market;
      case OrderTypeIntDto.Limit:
        return OrderTypeDto.Limit;
      case OrderTypeIntDto.Stop_Market:
        return OrderTypeDto.StopMarket;
      case OrderTypeIntDto.Stop_Limit:
        return OrderTypeDto.StopLimit;
      case OrderTypeIntDto.Market_On_Close:
        return OrderTypeDto.MarketOnClose;
      default:
        return OrderTypeDto.Market;
    }
  }

  private mapTypeEnumToLabel(type: OrderTypeDto): OrderTypeLabel {
    switch (type) {
      case OrderTypeDto.Market:
        return OrderTypeLabel.Market;
      case OrderTypeDto.Limit:
        return OrderTypeLabel.Limit;
      case OrderTypeDto.StopMarket:
        return OrderTypeLabel.StopMarket;
      case OrderTypeDto.StopLimit:
        return OrderTypeLabel.StopLimit;
      case OrderTypeDto.MarketOnClose:
        return OrderTypeLabel.MarketOnClose;
      default:
        return OrderTypeLabel.Market;
    }
  }

  get value(): OrderTypeDto {
    return this._value;
  }

  get label(): OrderTypeLabel {
    return this._label;
  }

  get isMarket(): boolean {
    return this._value === OrderTypeDto.Market || this._value === OrderTypeDto.MarketOnClose;
  }

  get isLimit(): boolean {
    return this._value === OrderTypeDto.Limit || this._value === OrderTypeDto.StopLimit;
  }

  get isStopMarket(): boolean {
    return this._value === OrderTypeDto.StopMarket;
  }

  get isStopLimit(): boolean {
    return this._value === OrderTypeDto.StopLimit;
  }
}

export class OrderAction {
  private _value: OrderActionDto;
  private _label: OrderActionLabel;

  constructor(action?: OrderActionIntDto | OrderActionDto) {
    this._value = isNumber(action) ? this.mapActioNumberToEnum(action) : (action as OrderActionDto);
    this._label = this.findLabel();
  }

  private mapActioNumberToEnum(action: OrderActionIntDto): OrderActionDto {
    return action === OrderActionIntDto.Buy ? OrderActionDto.Buy : OrderActionDto.Sell;
  }

  private findLabel(): OrderActionLabel {
    return this._value === OrderActionDto.Buy ? OrderActionLabel.Buy : OrderActionLabel.Sell;
  }

  get value(): OrderActionDto {
    return this._value;
  }

  get label(): OrderActionLabel {
    return this._label;
  }

  get isBuy(): boolean {
    return this._value === OrderActionDto.Buy;
  }

  get isSell(): boolean {
    return this._value === OrderActionDto.Sell;
  }
}

export class OrderDuration {
  private _value: OrderDurationDto;
  private _label: OrderDurationLabel;

  constructor(duration: OrderDurationIntDto | OrderDurationDto) {
    this._value = isNumber(duration) ? this.mapDurationNumberToEnum(duration) : (duration as OrderDurationDto);
    this._label = this.mapDurationEnumToLabel(this._value);
  }

  private mapDurationNumberToEnum(duration: OrderDurationIntDto): OrderDurationDto {
    return duration === OrderDurationIntDto.Day ? OrderDurationDto.Day : OrderDurationDto.GTC;
  }

  private mapDurationEnumToLabel(duration: OrderDurationDto): OrderDurationLabel {
    switch (duration) {
      case OrderDurationDto.Day:
        return OrderDurationLabel.Day;
      case OrderDurationDto.GTC:
        return OrderDurationLabel.GTC;
    }
  }

  get value(): OrderDurationDto {
    return this._value;
  }

  get label(): OrderDurationLabel {
    return this._label;
  }
}

export interface OrderEvent {
  description?: string;
  fillPrice: number;
  fillQuantity: number;
  limit?: number;
  stop?: number;
  status: OrderEventStatus;
  symbol?: string;
  createdAt: string;
}

export interface Order {
  id: string;
  symbol: string;
  description: string;
  updatedAt: string;
  quantity: number;
  filledQuantity: number;
  isPartial: boolean;
  price: number;
  totalCost: number;
  type: OrderType;
  action: OrderAction;
  status: OrderStatus;
  duration: OrderDuration;
  commission: number;
  limit?: number;
  stop?: number;
}

export interface OrderDetails extends Order {
  events: OrderEvent[];
}

export interface SecurityOrderView {
  type?: OrderTypeDto;
  action?: OrderActionDto;
  duration?: OrderDurationDto;
  quantity?: number;
  price?: number;
  stop?: number;
  estimatedTotal?: number;
}
